import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { formatPrice, formatHour, SPORT_COLORS, hexToRgba } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Calendar, Clock, Users, Plus, Activity, MapPin } from "lucide-react";
import { toast } from "sonner";

const SPORTS = ["all", "football", "basketball", "tennis", "padel", "pickleball"];

export default function PlayBrowse() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sport, setSport] = useState("all");
  const [showMine, setShowMine] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (sport !== "all") params.sport = sport;
    api.get("/play", { params }).then((r) => {
      let filtered = r.data;
      if (showMine && user) {
        filtered = filtered.filter(
          (s) => s.host_id === user.id || s.claims?.some((c) => c.captain_id === user.id)
        );
      }
      setSessions(filtered);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [sport, showMine, user]);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-10">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="label-eyebrow text-volt mb-2">{t("play.eyebrow")}</div>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight">{t("play.title")}</h1>
            <p className="text-sm text-muted-foreground/80 mt-2">{t("play.subtitle")}</p>
          </div>
          {user && (
            <button
              onClick={() => navigate("/play/create")}
              className="btn-volt rounded-sm px-5 py-3 text-xs flex items-center gap-2 shrink-0"
            >
              <Plus className="h-4 w-4" /> {t("play.create")}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {SPORTS.map((s) => (
            <button
              key={s}
              onClick={() => setSport(s)}
              className={`px-3 py-2 text-[10px] tracking-widest uppercase font-bold border transition-all ${
                sport === s
                  ? "border-volt bg-volt text-primary-foreground"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
              }`}
              style={sport === s && s !== "all" ? { borderColor: SPORT_COLORS[s]?.bg, backgroundColor: hexToRgba(SPORT_COLORS[s]?.bg, 0.15), color: SPORT_COLORS[s]?.bg } : {}}
            >
              {s === "all" ? t("play.all_sports") : t("home.sports." + s)}
            </button>
          ))}
          {user && (
            <button
              onClick={() => setShowMine(!showMine)}
              className={`px-3 py-2 text-[10px] tracking-widest uppercase font-bold border transition-all ${
                showMine
                  ? "border-volt text-volt bg-volt/10"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("play.my_sessions")}
            </button>
          )}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-border/30 bg-card p-6 animate-pulse">
                <div className="h-6 bg-muted w-1/3 mb-3" />
                <div className="h-4 bg-muted w-2/3 mb-2" />
                <div className="h-4 bg-muted w-1/4" />
              </div>
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <Activity className="h-10 w-10 text-muted-foreground/40 mx-auto mb-4" />
            <p className="font-heading text-2xl text-muted-foreground/80 mb-2">{t("play.no_sessions")}</p>
            <p className="text-sm text-muted-foreground/60 mb-6">{t("play.no_sessions_hint")}</p>
            {user && (
              <button
                onClick={() => navigate("/play/create")}
                className="btn-volt rounded-sm px-5 py-3 text-xs inline-flex items-center gap-2"
              >
                <Plus className="h-4 w-4" /> {t("play.create")}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map((s) => {
              const remaining = s.max_players - s.filled_count;
              const isHost = user?.id === s.host_id;
              const hasJoined = s.claims?.some((c) => c.captain_id === user?.id);
              const isConfirmed = s.status === "confirmed";
              const sessionEnd = new Date(s.date);
              sessionEnd.setHours(s.end_hour || 0, 0, 0, 0);
              const isPast = new Date() >= sessionEnd;
              return (
                <Link
                  key={s.id}
                  to={`/play/${s.id}`}
                  className="block border border-border bg-card p-5 hover:border-volt/50 transition-colors"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-10 h-10 flex items-center justify-center shrink-0"
                        style={{ background: hexToRgba(SPORT_COLORS[s.sport]?.bg, 0.15) }}
                      >
                        <Activity className="h-5 w-5" style={{ color: SPORT_COLORS[s.sport]?.bg }} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-heading text-xl tracking-tight">{s.court_name}</div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> {s.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {formatHour(s.start_hour)} – {formatHour(s.end_hour)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> {t("home.sports." + s.sport)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-end">
                        <div className="text-xs text-muted-foreground/80">{t("play.per_player")}</div>
                        <div className="font-heading text-2xl text-volt">{formatPrice(s.price_per_player, s.currency)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    <div className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 text-muted-foreground/80" />
                      <span className="text-xs text-muted-foreground">{s.filled_count}/{s.max_players}</span>
                      {remaining > 0 ? (
                        <span className="text-[10px] text-emerald-400">{remaining} {t("play.spots_left")}</span>
                      ) : (
                        <span className="text-[10px] text-volt font-bold">{t("play.full")}</span>
                      )}
                    </div>
                    {s.skill_level !== "all" && (
                      <span className="text-[9px] tracking-widest uppercase border border-border px-1.5 py-0.5 text-muted-foreground/80">
                        {t("play.level_" + s.skill_level)}
                      </span>
                    )}
                    {s.team_mode && (
                      <span className="text-[9px] tracking-widest uppercase border border-volt/30 px-1.5 py-0.5 text-volt">
                        {t("play.team_mode")}
                      </span>
                    )}
                    {s.recurring_group_id && (
                      <span className="text-[9px] tracking-widest uppercase border border-purple-400/30 px-1.5 py-0.5 text-purple-400">
                        {t("play.recurring_weekly")}
                      </span>
                    )}
                    {isConfirmed && (
                      <span className="text-[9px] tracking-widest uppercase border border-emerald-400/30 px-1.5 py-0.5 text-emerald-400">
                        {t("play.confirmed")}
                      </span>
                    )}
                    {isPast && (
                      <span className="text-[9px] tracking-widest uppercase border border-border px-1.5 py-0.5 text-muted-foreground/80">
                        {t("play.session_ended")}
                      </span>
                    )}
                    {s.min_players_per_claim > 1 && (
                      <span className="text-[9px] tracking-widest uppercase border border-border px-1.5 py-0.5 text-muted-foreground/80">
                        {t("play.join_min_badge", { n: s.min_players_per_claim })}
                      </span>
                    )}
                    {isHost && (
                      <span className="text-[9px] tracking-widest uppercase text-yellow-400/70">
                        {t("play.you_host")}
                      </span>
                    )}
                    {hasJoined && !isHost && (
                      <span className="text-[9px] tracking-widest uppercase text-volt">
                        {t("play.you_joined")}
                      </span>
                    )}
                  </div>
                    {s.recurring_group_id && user && s.status === "open" && !isHost && !hasJoined && (
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          try {
                            await api.post(`/play/${s.id}/subscribe`);
                            load();
                          } catch (err) {
                            toast.error(formatApiError(err.response?.data?.detail));
                          }
                        }}
                        className="mt-2 text-[10px] tracking-widest uppercase text-purple-400 hover:text-purple-300 border border-purple-400/20 px-2 py-1"
                      >
                        {t("play.subscribe")}
                      </button>
                    )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
